import { useState, type FormEvent, type ReactNode } from "react";
import { api, API_URL, downloadFile } from "../lib/api";
import { useFetch } from "../lib/useFetch";
import type { ClientSummary, ClientDetail, Role, StaffMember } from "../lib/types";

const ROLES: Role[] = [
  "super_admin",
  "it_manager",
  "l2_support",
  "l1_support",
  "finance",
];

const ORIGIN = API_URL || window.location.origin;

function copy(text: string) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

export default function Admin() {
  const clients = useFetch<ClientSummary[]>("/api/clients");
  const staff = useFetch<StaffMember[]>("/api/staff");
  const [err, setErr] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Request failed");
    }
  };

  return (
    <>
      <div className="topbar">
        <h1>Admin</h1>
      </div>
      {err && <div className="error" style={{ marginBottom: 12 }}>{err}</div>}

      <ClientsCard
        clients={clients.data ?? []}
        error={clients.error}
        onChange={clients.reload}
        run={run}
      />
      <div style={{ height: 20 }} />
      <StaffCard
        staff={staff.data ?? []}
        clients={clients.data ?? []}
        error={staff.error}
        onChange={staff.reload}
        run={run}
      />
    </>
  );
}

/* ── Clients ─────────────────────────────────────────────────────────────── */

function ClientsCard({
  clients,
  error,
  onChange,
  run,
}: {
  clients: ClientSummary[];
  error: string | null;
  onChange: () => void;
  run: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [afterHoursMsg, setAfter] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [token, setToken] = useState<Record<string, string>>({});

  async function create(e: FormEvent) {
    e.preventDefault();
    await run(async () => {
      await api("/api/clients", { method: "POST", body: { name, afterHoursMsg: afterHoursMsg || undefined } });
      setName("");
      setAfter("");
      setOpen(false);
      onChange();
    });
  }

  async function reveal(id: string) {
    setExpanded(expanded === id ? null : id);
    if (!token[id] && expanded !== id) {
      await run(async () => {
        const c = await api<ClientDetail>(`/api/clients/${id}`);
        if (c.agentToken) setToken((t) => ({ ...t, [id]: c.agentToken! }));
      });
    }
  }

  async function rotate(id: string, cname: string) {
    if (!confirm(`Rotate the agent token for "${cname}"?\nEvery agent already deployed for this client will stop reporting until re-installed with the new token.`))
      return;
    await run(async () => {
      const r = await api<{ agentToken: string }>(`/api/clients/${id}/rotate-token`, { method: "POST" });
      setToken((t) => ({ ...t, [id]: r.agentToken }));
    });
  }

  async function toggle(c: ClientSummary) {
    await run(async () => {
      await api(`/api/clients/${c.id}`, { method: "PATCH", body: { isActive: !c.isActive } });
      onChange();
    });
  }

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Clients</h2>
        <div className="spacer" />
        <button className={open ? "" : "primary"} onClick={() => setOpen(!open)}>
          {open ? "Cancel" : "New client"}
        </button>
      </div>

      {open && (
        <form className="grid" onSubmit={create} style={{ marginBottom: 14, maxWidth: 480 }}>
          <label>
            <div className="muted">Client name</div>
            <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
          </label>
          <label>
            <div className="muted">After-hours message (optional)</div>
            <input value={afterHoursMsg} onChange={(e) => setAfter(e.target.value)} />
          </label>
          <button className="primary">Create client + agent token</button>
        </form>
      )}

      {error && <div className="error">{error}</div>}

      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Devices</th>
            <th>Tickets</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {clients.map((c) => (
            <FragmentRow key={c.id}>
              <tr>
                <td>{c.name}</td>
                <td>{c._count.devices}</td>
                <td>{c._count.tickets}</td>
                <td>
                  <span className={`pill ${c.isActive ? "ok" : "danger"}`}>
                    {c.isActive ? "active" : "inactive"}
                  </span>
                </td>
                <td>
                  <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                    <button
                      className="primary"
                      onClick={() =>
                        run(() =>
                          downloadFile(
                            `/api/clients/${c.id}/agent-package`,
                            `MSPAgent-${c.slug}.zip`,
                          ),
                        )
                      }
                    >
                      Download agent
                    </button>
                    <button onClick={() => reveal(c.id)}>
                      {expanded === c.id ? "Hide token" : "Agent token"}
                    </button>
                    <button onClick={() => toggle(c)}>
                      {c.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </td>
              </tr>
              {expanded === c.id && (
                <tr>
                  <td colSpan={5} style={{ background: "var(--bg-elev-2)" }}>
                    <TokenPanel
                      token={token[c.id]}
                      clientName={c.name}
                      onRotate={() => rotate(c.id, c.name)}
                    />
                  </td>
                </tr>
              )}
            </FragmentRow>
          ))}
          {clients.length === 0 && (
            <tr>
              <td colSpan={5} className="muted">No clients yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function TokenPanel({
  token,
  clientName,
  onRotate,
}: {
  token: string | undefined;
  clientName: string;
  onRotate: () => void;
}) {
  if (!token) return <div className="muted">Loading token…</div>;
  const install =
    `.\\Install-MSPAgent.ps1 -ServerUrl "${ORIGIN}" -AgentToken "${token}" ` +
    `-ClientName "${clientName}" -IntervalMinutes 5`;
  return (
    <div className="grid" style={{ gap: 8, padding: "4px 0" }}>
      <div>
        <div className="muted">Agent token</div>
        <code style={{ wordBreak: "break-all", fontSize: 12 }}>{token}</code>
      </div>
      <div className="row" style={{ gap: 6 }}>
        <button onClick={() => copy(token)}>Copy token</button>
        <button onClick={() => copy(install)}>Copy install command</button>
        <button onClick={onRotate}>Rotate token</button>
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        Easiest: <b>Download agent</b> above — the zip has this token baked in; on the PC,
        unzip and right-click <code>Install.cmd</code> → Run as administrator. The command
        line here is the manual / GPO equivalent.
      </div>
    </div>
  );
}

/* ── Staff ───────────────────────────────────────────────────────────────── */

function StaffCard({
  staff,
  clients,
  error,
  onChange,
  run,
}: {
  staff: StaffMember[];
  clients: ClientSummary[];
  error: string | null;
  onChange: () => void;
  run: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "l1_support" as Role });
  const [editing, setEditing] = useState<string | null>(null);

  async function create(e: FormEvent) {
    e.preventDefault();
    await run(async () => {
      await api("/api/staff", { method: "POST", body: form });
      setForm({ name: "", email: "", password: "", role: "l1_support" });
      setOpen(false);
      onChange();
    });
  }

  async function patch(id: string, body: Record<string, unknown>) {
    await run(async () => {
      await api(`/api/staff/${id}`, { method: "PATCH", body });
      onChange();
    });
  }

  async function resetPw(id: string) {
    const pw = prompt("New password (min 8 chars):");
    if (!pw) return;
    await patch(id, { password: pw });
  }

  async function setClients(id: string, clientIds: string[]) {
    await run(async () => {
      await api(`/api/staff/${id}/clients`, { method: "PUT", body: { clientIds } });
      onChange();
    });
  }

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Staff</h2>
        <div className="spacer" />
        <button className={open ? "" : "primary"} onClick={() => setOpen(!open)}>
          {open ? "Cancel" : "New staff"}
        </button>
      </div>

      {open && (
        <form className="grid" onSubmit={create} style={{ marginBottom: 14, maxWidth: 480 }}>
          <label>
            <div className="muted">Name</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label>
            <div className="muted">Email</div>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </label>
          <label>
            <div className="muted">Temporary password (min 8)</div>
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
          </label>
          <label>
            <div className="muted">Role</div>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Role })}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <button className="primary">Create staff</button>
        </form>
      )}

      {error && <div className="error">{error}</div>}

      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Clients</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {staff.map((s) => (
            <FragmentRow key={s.id}>
              <tr>
                <td>{s.name}</td>
                <td className="muted">{s.email}</td>
                <td>
                  <select
                    value={s.role}
                    onChange={(e) => patch(s.id, { role: e.target.value })}
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td>
                  <span className={`pill ${s.isActive ? "ok" : "danger"}`}>
                    {s.isActive ? "active" : "disabled"}
                  </span>
                </td>
                <td className="muted">
                  {s.role === "super_admin"
                    ? "all"
                    : s.clientLinks.map((l) => l.client.name).join(", ") || "—"}
                </td>
                <td>
                  <div className="row" style={{ gap: 6 }}>
                    <button onClick={() => setEditing(editing === s.id ? null : s.id)}>
                      Clients
                    </button>
                    <button onClick={() => patch(s.id, { isActive: !s.isActive })}>
                      {s.isActive ? "Disable" : "Enable"}
                    </button>
                    <button onClick={() => resetPw(s.id)}>Reset pw</button>
                  </div>
                </td>
              </tr>
              {editing === s.id && (
                <tr>
                  <td colSpan={6} style={{ background: "var(--bg-elev-2)" }}>
                    <ClientAssign
                      all={clients}
                      selected={s.clientLinks.map((l) => l.clientId)}
                      disabled={s.role === "super_admin"}
                      onSave={(ids) => setClients(s.id, ids)}
                    />
                  </td>
                </tr>
              )}
            </FragmentRow>
          ))}
          {staff.length === 0 && (
            <tr>
              <td colSpan={6} className="muted">No staff yet.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ClientAssign({
  all,
  selected,
  disabled,
  onSave,
}: {
  all: ClientSummary[];
  selected: string[];
  disabled: boolean;
  onSave: (ids: string[]) => void;
}) {
  const [ids, setIds] = useState<string[]>(selected);
  if (disabled) {
    return <div className="muted" style={{ padding: "6px 0" }}>super_admin sees every client.</div>;
  }
  return (
    <div className="grid" style={{ gap: 8, padding: "6px 0" }}>
      <div className="row" style={{ flexWrap: "wrap", gap: 12 }}>
        {all.map((c) => (
          <label key={c.id} className="row" style={{ gap: 6, width: "auto" }}>
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={ids.includes(c.id)}
              onChange={(e) =>
                setIds(
                  e.target.checked
                    ? [...ids, c.id]
                    : ids.filter((x) => x !== c.id),
                )
              }
            />
            {c.name}
          </label>
        ))}
        {all.length === 0 && <span className="muted">No clients to assign.</span>}
      </div>
      <div>
        <button className="primary" onClick={() => onSave(ids)}>
          Save assignments
        </button>
      </div>
    </div>
  );
}

/* Small helper so a row can render an extra <tr> without a wrapper element. */
function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
